import { useMemo } from 'react';

import { CartesianGrid, LineChart, XAxis, YAxis } from 'recharts';
import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import type { AxisDomain } from 'recharts/types/util/types';

import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart';
import { formatDate } from '@/util/dateUtils';

import {
  EFFECTIVENESS_METRIC_KEYS,
  type EffectivenessMetricKey,
  type EffectivenessTimeseriesPoint,
} from './effectivenessChartConfig';
import { formatDurationFromSeconds } from '../../utils/formatDuration';
import { padSeriesToPeriod, type PeriodRange } from '../../utils/padSeriesToPeriod';
import {
  CHART_COLOR,
  COMPARISON_CHART_COLOR,
  renderTimeseriesLines,
} from '../TimeseriesChart/renderTimeseriesLines';
import {
  MetricTooltipCard,
  MetricTooltipRow,
  MetricTooltipSectionLabel,
} from '../tooltip/MetricTooltipCard';
const Y_DOMAIN: AxisDomain = [0, 'auto'];
// Every effectiveness metric is a duration average, so empty days always break
// the line (gap) rather than fabricating a 0.
const FILL_POLICY = 'gap' as const;

// Drop Recharts' tick `index` arg so it isn't read as subMinuteDecimals.
const formatDurationTick = (value: number) => formatDurationFromSeconds(value);

type PaddedPoint = EffectivenessTimeseriesPoint & { isIsolated?: boolean };
type MergedPoint = PaddedPoint & { comparisonValue?: number | null };

const EffectivenessTooltip = ({
  active,
  payload,
  label,
  metric,
  t,
  showComparison,
}: TooltipProps<ValueType, NameType> & {
  metric: EffectivenessMetricKey;
  t: (key: string) => string;
  showComparison?: boolean;
}) => {
  if (!active || !payload?.length) {
    return null;
  }
  const value = payload.find((p) => p.dataKey === 'value')?.value as number | undefined;
  if (value == null) {
    return null;
  }
  const comparisonValue = payload.find((p) => p.dataKey === 'comparisonValue')?.value as
    number | undefined;
  const formattedDate = formatDate(label as string, 'MMM d, yyyy');
  const metricLabel = t(EFFECTIVENESS_METRIC_KEYS[metric].label);

  return (
    <MetricTooltipCard title={formattedDate}>
      <MetricTooltipRow label={metricLabel} value={formatDurationFromSeconds(value, 1)} />
      {showComparison && comparisonValue != null && (
        <>
          <MetricTooltipSectionLabel>
            {t('analytics.overview_new.previous_period')}
          </MetricTooltipSectionLabel>
          <MetricTooltipRow
            label={metricLabel}
            value={formatDurationFromSeconds(comparisonValue, 1)}
          />
        </>
      )}
    </MetricTooltipCard>
  );
};

const mergeTimeseriesData = (primary: PaddedPoint[], comparison: PaddedPoint[]): MergedPoint[] =>
  primary.map((point, index) => ({ ...point, comparisonValue: comparison[index]?.value ?? null }));

export const HumanAgentsEffectivenessChart = ({
  data,
  comparisonData,
  metric,
  t,
  period,
  comparisonPeriod,
}: {
  data: EffectivenessTimeseriesPoint[];
  comparisonData?: EffectivenessTimeseriesPoint[];
  metric: EffectivenessMetricKey;
  t: (key: string) => string;
  /** The selected date range; drives the x-axis so every day renders, data or not. */
  period: PeriodRange;
  /** The previous-period range. Equal length to `period` by construction; required so the
      comparison series can never render without merged comparison values. */
  comparisonPeriod: PeriodRange;
}) => {
  const showComparison = !!comparisonData && comparisonData.length > 0;

  const chartData = useMemo<MergedPoint[] | PaddedPoint[]>(() => {
    const paddedPrimary = padSeriesToPeriod(data, period, FILL_POLICY);
    if (showComparison) {
      return mergeTimeseriesData(
        paddedPrimary,
        padSeriesToPeriod(comparisonData ?? [], comparisonPeriod, FILL_POLICY)
      );
    }
    return paddedPrimary;
  }, [data, comparisonData, showComparison, period, comparisonPeriod]);

  const chartConfig = useMemo<ChartConfig>(() => {
    const base: ChartConfig = {
      value: { label: t(EFFECTIVENESS_METRIC_KEYS[metric].label), color: CHART_COLOR },
    };
    if (showComparison) {
      base.comparisonValue = {
        label: t('analytics.overview_new.previous_period'),
        color: COMPARISON_CHART_COLOR,
      };
    }
    return base;
  }, [metric, t, showComparison]);

  return (
    <ChartContainer config={chartConfig} className="aspect-4/1 w-full">
      <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value) => formatDate(value, 'MMM d')}
          interval="preserveStartEnd"
          minTickGap={40}
        />
        <YAxis
          domain={Y_DOMAIN}
          tickCount={5}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          width={80}
          tickFormatter={formatDurationTick}
        />
        <ChartTooltip
          content={<EffectivenessTooltip metric={metric} t={t} showComparison={showComparison} />}
        />
        {renderTimeseriesLines({ fill: FILL_POLICY, showComparison })}
      </LineChart>
    </ChartContainer>
  );
};
